<template>
  <div class="dtp-root" :class="label ? 'mt-4' : ''" ref="root">
    <!-- Trigger input -->
    <div class="dtp-trigger" @click="toggle" :class="{ 'dtp-trigger--open': open, 'dtp-trigger--disabled': disabled }">
      <span class="dtp-trigger__value" :class="{ 'dtp-trigger__value--empty': !displayValue }">
        {{ displayValue || label || 'Select date…' }}
      </span>
      <span v-if="label && displayValue" class="dtp-trigger__label">{{ label }}</span>
      <button v-if="displayValue && !disabled" class="dtp-trigger__clear" @click.stop="clear" tabindex="-1" title="Clear">
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <path d="M2 2l8 8M10 2l-8 8"/>
        </svg>
      </button>
      <svg class="dtp-trigger__icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
        <rect x="2" y="3" width="12" height="11" rx="2"/>
        <path d="M5 1v2M11 1v2M2 7h12"/>
      </svg>
    </div>

    <!-- Dropdown panel — teleported to body to escape overflow:hidden parents -->
    <Teleport to="body">
    <div v-if="open" class="dtp-panel" :style="panelStyle" ref="panel">
      <!-- Month navigation -->
      <div class="dtp-header">
        <button class="dtp-nav" @click="prevMonth">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10 3L5 8l5 5"/>
          </svg>
        </button>
        <span class="dtp-header__title">{{ monthLabel }} {{ viewYear }}</span>
        <button class="dtp-nav" @click="nextMonth">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 3l5 5-5 5"/>
          </svg>
        </button>
      </div>

      <!-- Day names -->
      <div class="dtp-daynames">
        <span v-for="d in dayNames" :key="d">{{ d }}</span>
      </div>

      <!-- Calendar grid -->
      <div class="dtp-grid">
        <button
          v-for="day in calendarDays"
          :key="day.key"
          class="dtp-day"
          :class="{
            'dtp-day--other': !day.inMonth,
            'dtp-day--today': day.isToday,
            'dtp-day--selected': day.isSelected,
            'dtp-day--range-start': day.isRangeStart,
            'dtp-day--range-end': day.isRangeEnd,
            'dtp-day--in-range': day.isInRange,
            'dtp-day--hover-range': day.isHoverRange,
          }"
          @click="selectDay(day)"
          @mouseenter="hoverDate = day.date"
          @mouseleave="hoverDate = null"
        >
          {{ day.num }}
        </button>
      </div>

      <!-- Time picker (single) -->
      <template v-if="!range">
        <div class="dtp-divider"></div>
        <div class="dtp-time">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style="width:13px;height:13px;opacity:0.4;flex-shrink:0;">
            <circle cx="8" cy="8" r="6"/>
            <path d="M8 5v3.2l2 2"/>
          </svg>
          <div class="dtp-time__field">
            <button class="dtp-time__step" @click="hour = clamp(hour - 1, 0, 23)">
              <svg viewBox="0 0 10 6"><path d="M1 5l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
            </button>
            <input ref="hourInput" class="dtp-time__input" type="text" inputmode="numeric" maxlength="2" :value="pad(hour)"
              @focus="$event.target.select()"
              @input="onTimeInput($event, 'hour', 23, 'minuteInput')"
              @blur="onTimeBlur($event, 'hour', 23)" />
            <button class="dtp-time__step" @click="hour = clamp(hour + 1, 0, 23)">
              <svg viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
            </button>
          </div>
          <span class="dtp-time__sep">:</span>
          <div class="dtp-time__field">
            <button class="dtp-time__step" @click="minute = clamp(minute - 5, 0, 59)">
              <svg viewBox="0 0 10 6"><path d="M1 5l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
            </button>
            <input ref="minuteInput" class="dtp-time__input" type="text" inputmode="numeric" maxlength="2" :value="pad(minute)"
              @focus="$event.target.select()"
              @input="onTimeInput($event, 'minute', 59, null)"
              @blur="onTimeBlur($event, 'minute', 59)" />
            <button class="dtp-time__step" @click="minute = clamp(minute + 5, 0, 59)">
              <svg viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
            </button>
          </div>
        </div>
      </template>

      <!-- Time picker (range) -->
      <template v-if="range && rangeStart && rangeEnd">
        <div class="dtp-divider"></div>
        <div class="dtp-range-times">
          <div class="dtp-range-time-col">
            <div class="dtp-range-time-label">Start</div>
            <div class="dtp-time">
              <div class="dtp-time__field">
                <button class="dtp-time__step" @click="startHour = clamp(startHour - 1, 0, 23)">
                  <svg viewBox="0 0 10 6"><path d="M1 5l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
                </button>
                <input ref="startHourInput" class="dtp-time__input" type="text" inputmode="numeric" maxlength="2" :value="pad(startHour)"
                  @focus="$event.target.select()"
                  @input="onTimeInput($event, 'startHour', 23, 'startMinuteInput')"
                  @blur="onTimeBlur($event, 'startHour', 23)" />
                <button class="dtp-time__step" @click="startHour = clamp(startHour + 1, 0, 23)">
                  <svg viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
                </button>
              </div>
              <span class="dtp-time__sep">:</span>
              <div class="dtp-time__field">
                <button class="dtp-time__step" @click="startMinute = clamp(startMinute - 5, 0, 59)">
                  <svg viewBox="0 0 10 6"><path d="M1 5l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
                </button>
                <input ref="startMinuteInput" class="dtp-time__input" type="text" inputmode="numeric" maxlength="2" :value="pad(startMinute)"
                  @focus="$event.target.select()"
                  @input="onTimeInput($event, 'startMinute', 59, null)"
                  @blur="onTimeBlur($event, 'startMinute', 59)" />
                <button class="dtp-time__step" @click="startMinute = clamp(startMinute + 5, 0, 59)">
                  <svg viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
                </button>
              </div>
            </div>
          </div>
          <div class="dtp-range-time-col">
            <div class="dtp-range-time-label">End</div>
            <div class="dtp-time">
              <div class="dtp-time__field">
                <button class="dtp-time__step" @click="endHour = clamp(endHour - 1, 0, 23)">
                  <svg viewBox="0 0 10 6"><path d="M1 5l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
                </button>
                <input ref="endHourInput" class="dtp-time__input" type="text" inputmode="numeric" maxlength="2" :value="pad(endHour)"
                  @focus="$event.target.select()"
                  @input="onTimeInput($event, 'endHour', 23, 'endMinuteInput')"
                  @blur="onTimeBlur($event, 'endHour', 23)" />
                <button class="dtp-time__step" @click="endHour = clamp(endHour + 1, 0, 23)">
                  <svg viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
                </button>
              </div>
              <span class="dtp-time__sep">:</span>
              <div class="dtp-time__field">
                <button class="dtp-time__step" @click="endMinute = clamp(endMinute - 5, 0, 59)">
                  <svg viewBox="0 0 10 6"><path d="M1 5l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
                </button>
                <input ref="endMinuteInput" class="dtp-time__input" type="text" inputmode="numeric" maxlength="2" :value="pad(endMinute)"
                  @focus="$event.target.select()"
                  @input="onTimeInput($event, 'endMinute', 59, null)"
                  @blur="onTimeBlur($event, 'endMinute', 59)" />
                <button class="dtp-time__step" @click="endMinute = clamp(endMinute + 5, 0, 59)">
                  <svg viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Footer -->
      <div class="dtp-footer">
        <span v-if="range && !rangeStart" class="dtp-footer__hint">Pick start date</span>
        <span v-else-if="range && !rangeEnd" class="dtp-footer__hint">Pick end date</span>
        <span v-else style="flex:1"></span>
        <button v-if="allowNoEnd && rangeStart" class="dtp-footer__no-end" @click="confirmNoEnd">
          No end date
        </button>
        <button class="dtp-footer__confirm" @click="confirm" :disabled="!canConfirm">
          Confirm
        </button>
      </div>
    </div>

    </Teleport>

    <div v-if="error" class="dtp-error">
      <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style="width:11px;height:11px;flex-shrink:0;">
        <circle cx="6" cy="6" r="5"/><path d="M6 3.5v3M6 8.5v.5"/>
      </svg>
      {{ error }}
    </div>
  </div>
</template>

<script>
  import moment from 'moment'

  export default {
    name: 'DateTimePicker',
    props: ['value', 'label', 'name', 'range', 'disabled', 'viewTime', 'error', 'allowNoEnd'],
    data: () => ({
      open: false,
      panelStyle: {},
      viewYear: new Date().getFullYear(),
      viewMonth: new Date().getMonth(),
      // single mode
      selectedDate: null,
      hour: 0,
      minute: 0,
      // range mode
      rangeStart: null,
      rangeEnd: null,
      rangePhase: 'start',
      startHour: 0,
      startMinute: 0,
      endHour: 23,
      endMinute: 59,
      hoverDate: null,
    }),
    mounted() {
      this.syncFromValue()
      document.addEventListener('mousedown', this.onOutsideClick)
    },
    beforeUnmount() {
      document.removeEventListener('mousedown', this.onOutsideClick)
    },
    watch: {
      value() { this.syncFromValue() }
    },
    computed: {
      dayNames() { return ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] },
      monthNames() {
        return ['January','February','March','April','May','June',
                'July','August','September','October','November','December']
      },
      monthLabel() { return this.monthNames[this.viewMonth] },
      calendarDays() {
        const year = this.viewYear
        const month = this.viewMonth
        const firstDay = new Date(year, month, 1)
        // Monday-first: getDay() returns 0=Sun, so shift
        let startDow = firstDay.getDay()
        startDow = startDow === 0 ? 6 : startDow - 1
        const days = []
        const today = this.startOfDay(new Date())

        for (let i = 0; i < 42; i++) {
          const date = new Date(year, month, 1 - startDow + i)
          const dateStr = this.startOfDay(date).toISOString()
          const inMonth = date.getMonth() === month

          let isSelected = false
          let isRangeStart = false
          let isRangeEnd = false
          let isInRange = false
          let isHoverRange = false

          if (!this.range && this.selectedDate) {
            isSelected = this.startOfDay(date).getTime() === this.startOfDay(this.selectedDate).getTime()
          }

          if (this.range) {
            const ts = date.getTime()
            const startTs = this.rangeStart ? this.startOfDay(this.rangeStart).getTime() : null
            const endTs = this.rangeEnd ? this.startOfDay(this.rangeEnd).getTime() : null
            const hoverTs = this.hoverDate ? this.startOfDay(this.hoverDate).getTime() : null

            isRangeStart = startTs !== null && ts === startTs
            isRangeEnd = endTs !== null && ts === endTs
            isSelected = isRangeStart || isRangeEnd

            if (startTs !== null && endTs !== null) {
              isInRange = ts > startTs && ts < endTs
            } else if (startTs !== null && !endTs && hoverTs !== null) {
              const lo = Math.min(startTs, hoverTs)
              const hi = Math.max(startTs, hoverTs)
              isInRange = ts > lo && ts < hi
              isHoverRange = ts === hoverTs
            }
          }

          days.push({
            key: dateStr + i,
            date,
            num: date.getDate(),
            inMonth,
            isToday: date.getTime() === today.getTime(),
            isSelected,
            isRangeStart,
            isRangeEnd,
            isInRange,
            isHoverRange,
          })
        }

        // Trim trailing empty rows
        while (days.length > 35 && !days.slice(35).some(d => d.inMonth)) {
          days.splice(35, 7)
        }
        return days
      },
      canConfirm() {
        if (this.range) return !!(this.rangeStart && (this.rangeEnd || this.allowNoEnd))
        return !!this.selectedDate
      },
      displayValue() {
        if (this.range) {
          const s = this.rangeStart
          const e = this.rangeEnd
          if (!s && !e) return null
          const fmt = (d, h, m) => moment(d).hours(h).minutes(m).format('D MMM HH:mm')
          if (s && !e) return 'From ' + fmt(s, this.startHour, this.startMinute) + ' →'
          return fmt(s, this.startHour, this.startMinute) + ' – ' + fmt(e, this.endHour, this.endMinute)
        }
        if (!this.selectedDate) return null
        return moment(this.selectedDate).hours(this.hour).minutes(this.minute).format('D MMM YYYY, HH:mm')
      },
    },
    methods: {
      startOfDay(date) {
        const d = new Date(date)
        d.setHours(0, 0, 0, 0)
        return d
      },
      pad(n) { return String(n).padStart(2, '0') },
      clamp(n, min, max) { return Math.min(max, Math.max(min, n)) },
      prevMonth() {
        if (this.viewMonth === 0) { this.viewMonth = 11; this.viewYear-- }
        else this.viewMonth--
      },
      nextMonth() {
        if (this.viewMonth === 11) { this.viewMonth = 0; this.viewYear++ }
        else this.viewMonth++
      },
      toggle() {
        if (this.disabled) return
        if (!this.open) this.updatePanelPosition()
        this.open = !this.open
      },
      updatePanelPosition() {
        this.$nextTick(() => {
          const trigger = this.$refs.root?.querySelector('.dtp-trigger')
          if (!trigger) return
          const rect = trigger.getBoundingClientRect()
          const spaceBelow = window.innerHeight - rect.bottom
          const panelHeight = 420 // approx max height
          const top = spaceBelow >= panelHeight || spaceBelow > rect.top
            ? rect.bottom + window.scrollY + 6
            : rect.top + window.scrollY - panelHeight - 6
          this.panelStyle = {
            position: 'absolute',
            top: top + 'px',
            left: rect.left + window.scrollX + 'px',
            width: '280px',
            zIndex: 10001,
          }
        })
      },
      syncFromValue() {
        if (this.range) {
          const arr = Array.isArray(this.value) ? this.value : [null, null]
          const s = arr[0] ? moment(arr[0]) : null
          const e = arr[1] ? moment(arr[1]) : null
          this.rangeStart = s ? s.toDate() : null
          this.rangeEnd = e ? e.toDate() : null
          if (s) { this.startHour = s.hours(); this.startMinute = s.minutes() }
          if (e) { this.endHour = e.hours(); this.endMinute = e.minutes() }
          const nav = s || e
          if (nav) { this.viewYear = nav.year(); this.viewMonth = nav.month() }
          this.rangePhase = this.rangeStart && !this.rangeEnd ? 'end' : 'start'
        } else {
          if (this.value) {
            const m = moment(this.value)
            this.selectedDate = m.toDate()
            this.hour = m.hours()
            this.minute = m.minutes()
            this.viewYear = m.year()
            this.viewMonth = m.month()
          } else {
            this.selectedDate = null
          }
        }
      },
      selectDay(day) {
        if (this.range) {
          if (this.rangePhase === 'start' || (this.rangeStart && this.rangeEnd)) {
            // Reset and start new range
            this.rangeStart = day.date
            this.rangeEnd = null
            this.rangePhase = 'end'
          } else {
            // Picking end
            if (day.date < this.rangeStart) {
              this.rangeEnd = this.rangeStart
              this.rangeStart = day.date
            } else {
              this.rangeEnd = day.date
            }
            this.rangePhase = 'start'
          }
        } else {
          this.selectedDate = day.date
          this.viewYear = day.date.getFullYear()
          this.viewMonth = day.date.getMonth()
        }
      },
      confirmNoEnd() {
        if (!this.rangeStart) return
        const s = moment(this.rangeStart).hours(this.startHour).minutes(this.startMinute).seconds(0)
        this.$emit('input', [s.format(), null])
        this.open = false
      },
      confirm() {
        if (!this.canConfirm) return
        if (this.range) {
          const s = moment(this.rangeStart).hours(this.startHour).minutes(this.startMinute).seconds(0)
          const e = this.rangeEnd
            ? moment(this.rangeEnd).hours(this.endHour).minutes(this.endMinute).seconds(0)
            : null
          this.$emit('input', [s.format(), e ? e.format() : null])
        } else {
          const m = moment(this.selectedDate).hours(this.hour).minutes(this.minute).seconds(0)
          this.$emit('input', m.format())
        }
        this.open = false
      },
      clear() {
        this.selectedDate = null
        this.rangeStart = null
        this.rangeEnd = null
        this.rangePhase = 'start'
        this.$emit('input', this.range ? [null, null] : null)
      },
      onOutsideClick(e) {
        if (!this.open) return
        const inRoot = this.$refs.root?.contains(e.target)
        const inPanel = this.$refs.panel?.contains(e.target)
        if (!inRoot && !inPanel) this.open = false
      },
      onTimeInput(event, field, max, nextRef) {
        const raw = event.target.value.replace(/\D/g, '').slice(0, 2)
        event.target.value = raw
        // Only update reactive state when 2 digits are entered — avoids Vue
        // re-rendering the input with a padded value (e.g. "5" → "05") while
        // the user is still typing the second digit.
        if (raw.length === 2) {
          this[field] = this.clamp(parseInt(raw, 10), 0, max)
          if (nextRef && this.$refs[nextRef]) {
            this.$nextTick(() => {
              this.$refs[nextRef].focus()
              this.$refs[nextRef].select()
            })
          }
        }
      },
      onTimeBlur(event, field, max) {
        const num = parseInt(event.target.value, 10)
        this[field] = this.clamp(isNaN(num) ? 0 : num, 0, max)
        event.target.value = this.pad(this[field])
      },
    }
  }
</script>

<style scoped>
/* Root */
.dtp-root {
  position: relative;
  display: inline-block;
  width: 100%;
}

/* Trigger */
.dtp-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-md);
  background: var(--app-bg-input);
  cursor: pointer;
  transition: border-color var(--app-transition), background var(--app-transition);
  min-height: 36px;
  user-select: none;
  position: relative;
}

.dtp-trigger:hover:not(.dtp-trigger--disabled) {
  border-color: rgba(0, 0, 0, 0.18);
}

.dtp-trigger--open {
  border-color: rgba(61, 106, 144, 0.5) !important; /* primary at 50% */
  background: rgba(0, 0, 0, 0.02);
}

.dtp-trigger--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dtp-trigger__value {
  flex: 1;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dtp-trigger__value--empty {
  color: rgba(0, 0, 0, 0.38);
  font-size: 0.875rem;
}

.dtp-trigger__label {
  position: absolute;
  top: -18px;
  left: 1px;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
  pointer-events: none;
  white-space: nowrap;
}

.dtp-trigger__clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  color: rgba(0, 0, 0, 0.3);
  transition: color 0.12s, background 0.12s;
  flex-shrink: 0;
}
.dtp-trigger__clear svg { width: 9px; height: 9px; }
.dtp-trigger__clear:hover { color: rgba(0, 0, 0, 0.7); background: rgba(0,0,0,0.07); }

.dtp-trigger__icon {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.3);
}

/* Panel — positioned via JS (teleported to body) */
.dtp-panel {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  padding: 12px;
  box-sizing: border-box;
}

/* Header */
.dtp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.dtp-header__title {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
}

.dtp-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
  color: rgba(0, 0, 0, 0.35);
  transition: color 0.12s, background 0.12s;
}
.dtp-nav svg { width: 14px; height: 14px; }
.dtp-nav:hover { color: rgba(0, 0, 0, 0.7); background: rgba(0,0,0,0.06); }

/* Day names */
.dtp-daynames {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 4px;
}
.dtp-daynames span {
  text-align: center;
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.35);
  padding: 2px 0 4px;
  letter-spacing: 0.03em;
}

/* Calendar grid */
.dtp-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.dtp-day {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.75);
  transition: background 0.1s, color 0.1s;
  padding: 0;
}

.dtp-day:hover {
  background: var(--primary-muted);
  color: var(--primary);
}

.dtp-day--other { color: rgba(0, 0, 0, 0.22); }
.dtp-day--other:hover { color: var(--primary); opacity: 0.5; }

.dtp-day--today {
  font-weight: 700;
  color: var(--primary);
}

.dtp-day--selected {
  background: var(--primary) !important;
  color: white !important;
  font-weight: 600;
}

.dtp-day--range-start {
  background: var(--primary) !important;
  color: white !important;
  border-radius: 6px 0 0 6px;
}

.dtp-day--range-end {
  background: var(--primary) !important;
  color: white !important;
  border-radius: 0 6px 6px 0;
}

.dtp-day--in-range {
  background: var(--primary-muted);
  color: var(--primary);
  border-radius: 0;
}

.dtp-day--hover-range {
  background: rgba(61, 106, 144, 0.15) !important;
  border-radius: 6px;
}

/* Divider */
.dtp-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.07);
  margin: 10px 0;
}

/* Time */
.dtp-time {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.dtp-time__field {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.dtp-time__step {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 18px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  padding: 0;
  color: rgba(0, 0, 0, 0.3);
  transition: color 0.1s, background 0.1s;
}
.dtp-time__step svg { width: 10px; height: 6px; }
.dtp-time__step:hover { color: rgba(0,0,0,0.65); background: rgba(0,0,0,0.06); }

.dtp-time__input {
  width: 40px;
  height: 32px;
  text-align: center;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.03);
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
  outline: none;
}
.dtp-time__input:focus { border-color: rgba(61, 106, 144, 0.5); } /* primary at 50% */

.dtp-time__sep {
  font-size: 1rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.4);
  line-height: 32px;
  margin-bottom: 20px;
}

/* Range time row */
.dtp-range-times {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.dtp-range-time-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.dtp-range-time-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.4);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* Footer */
.dtp-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.07);
}

.dtp-footer__hint {
  flex: 1;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.4);
  font-style: italic;
}

.dtp-footer__no-end {
  display: inline-flex;
  align-items: center;
  padding: 0 10px;
  height: 30px;
  background: none;
  color: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.12s, border-color 0.12s;
}
.dtp-footer__no-end:hover { color: rgba(0, 0, 0, 0.7); border-color: rgba(0, 0, 0, 0.25); }

.dtp-footer__confirm {
  display: inline-flex;
  align-items: center;
  padding: 0 14px;
  height: 30px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}
.dtp-footer__confirm:hover { opacity: 0.88; }
.dtp-footer__confirm:disabled { opacity: 0.4; cursor: not-allowed; }

/* Error */
.dtp-error {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.78rem;
  color: #b03a3a;
  margin-top: 4px;
}
</style>
